import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, map, takeUntil } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import { ApiState } from '../../model/api-state.model';
import { AppType } from '../../model/app-type.model';
import { ProcessType } from '../../model/process-type.model';
import { FormsService } from '../../services/forms.service';
import { PersistStorageKeys, PersistStorageService } from '../../services/persist-storage.service';
import { AppState } from '../../store';
import * as fromConfig from '../../store/config/config.reducer';
import * as fromProcess from '../../store/process/process.reducer';
import { TrackingService } from '../../tracking/tracking.service';

const IFRAME_HEIGHT_TIMEOUT = 200;

@Component({
  selector: 'app-page-loader',
  templateUrl: './loader.component.html',
})
export class PageLoaderComponent implements OnInit, OnDestroy {
  isAppDirect = environment.app === AppType.Direct;
  configState$ = this.store.select(fromConfig.selectConfigState);
  formsServiceHydrationState$ = this.formsService.hydrationState$;
  appLoadingState$ = combineLatest([this.configState$, this.formsServiceHydrationState$]).pipe(
    map(([configState, formsServiceHydrationState]) => {
      if (configState === ApiState.pending || formsServiceHydrationState === ApiState.pending) {
        return ApiState.pending;
      } else if (configState === ApiState.error || formsServiceHydrationState === ApiState.error) {
        return ApiState.error;
      } else if (configState === ApiState.success && formsServiceHydrationState === ApiState.success) {
        return ApiState.success;
      } else {
        return ApiState.idle;
      }
    }),
  );

  ngDestroy$ = new Subject<void>();

  apiState = ApiState;

  constructor(
    private store: Store<AppState>,
    private formsService: FormsService,
    private persistStorage: PersistStorageService,
    private trackingService: TrackingService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromConfig.actions.fetchStart());

    this.appLoadingState$.pipe(takeUntil(this.ngDestroy$)).subscribe((state) => {
      this.persistStorage.setString(PersistStorageKeys.AppLoaded, state, 'session');

      if (state === ApiState.success) {
        this.implementFrameHeight();

        const path = this.persistStorage.getString(PersistStorageKeys.Path, 'session');
        if (this.isAppDirect && path) {
          this.store.dispatch(fromProcess.actions.setKind({ kind: path as ProcessType }));
          this.trackingService.insuranceSubject(path as ProcessType);
          void this.router.navigate(['page', 'step2']);
        } else {
          const entryUrl = this.persistStorage.getString(PersistStorageKeys.EntryUrl, 'session');
          void this.router.navigate(entryUrl ? entryUrl.split('/') : ['page', 'step1']);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  private implementFrameHeight(): void {
    try {
      parent.postMessage({ type: 'isLoading', isLoading: false }, environment.baseApiUrl);
      setTimeout(() => {
        const height = Math.max(document.body.offsetHeight, document.body.scrollHeight);
        parent.postMessage({ type: 'iframeHeight', height }, environment.baseApiUrl);
      }, IFRAME_HEIGHT_TIMEOUT);
    } catch (e) {
      console.error(e);
    }
  }
}
